// src/pages/SupportPage.js
import React, { useState, useMemo, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faCheck,
    faExclamationTriangle,
    faBug,
    faCreditCard,
    faShoppingCart,
    faWifi,
    faQuestionCircle,
    faChevronRight,
    faSpinner,
    faCheckCircle,
    faTimes,
    faClipboardList,
    faHeadset,
    faTools,
    faMobileAlt,
    faServer,
    faExclamationCircle,
    faQrcode,
    faTruck,
    faFileInvoiceDollar,
    faPlug,
    faBoxOpen,
    faComments,
    faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import BottomMenu from "../components/BottomMenu";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { QUESTION_GROUPS } from "../services/questions";

const API_BASE = process.env.REACT_APP_API_BASE ?? "https://boxmealadminapinew.azurewebsites.net";

// Category icons mapping - specifika ikoner baserat på innehåll
const getCategoryIcon = (key) => {
    const iconMap = {
        technical: faTools,
        payment: faCreditCard,
        order: faShoppingCart,
        connection: faWifi,
        product: faBoxOpen,
        app: faMobileAlt,
        delivery: faTruck,
        billing: faFileInvoiceDollar,
        network: faPlug,
        hardware: faServer,
        support: faHeadset,
        bug: faBug,
        qr: faQrcode,
        chat: faComments,
        other: faQuestionCircle,
    };
    return iconMap[key] || faQuestionCircle;
};

// Category colors - matchar kontaktsidans färgschema
const getCategoryColor = (key) => {
    const colorMap = {
        technical: "#374151",
        payment: "#374151",
        order: "#374151",
        connection: "#374151",
        product: "#374151",
        app: "#374151",
        delivery: "#374151",
        billing: "#374151",
        network: "#374151",
        hardware: "#374151",
        support: "#374151",
        bug: "#374151",
        other: "#374151",
    };
    return colorMap[key] || "#374151";
};

function ensureConsoleBuffer(max = 200) {
    if (typeof window === "undefined") return;
    if (window.__qrConsolePatched) return;
    window.__qrConsolePatched = true;

    const buf = [];
    const wrap = (level) => {
        const orig = console[level]?.bind(console);
        console[level] = (...args) => {
            try {
                buf.push({
                    ts: Date.now(),
                    level,
                    msg: args
                        .map((a) => {
                            try {
                                return typeof a === "string" ? a : JSON.stringify(a);
                            } catch {
                                return String(a);
                            }
                        })
                        .join(" "),
                });
                if (buf.length > max) buf.shift();
            } catch {}
            if (orig) orig(...args);
        };
    };
    ["log", "info", "warn", "error"].forEach(wrap);
    window.__qrConsoleBuffer = buf;
}

const safe = (v) =>
    String(v ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");

const dt = (ms) => (ms ? new Date(ms).toLocaleString() : "-");

function buildSupportDiagnostics({ selectedFridgeId, categoryKey, subject, answers, user }) {
    // Device snapshot
    const device = {
        ua: navigator.userAgent,
        lang: navigator.language,
        platform: navigator.platform,
        viewport: { w: window.innerWidth, h: window.innerHeight },
        screen: { w: window.screen?.width, h: window.screen?.height, dpr: window.devicePixelRatio },
        connection:
            navigator.connection && {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink,
                rtt: navigator.connection.rtt,
                saveData: navigator.connection.saveData,
            },
    };

    // App/page snapshot
    const app = {
        path: window.location.pathname,
        url: window.location.href,
        visibility: document.visibilityState,
        localStorage: {
            selectedFridgeId: localStorage.getItem("selectedFridgeId"),
            paymentStatus: localStorage.getItem("paymentStatus"),
            paymentConfirmed: localStorage.getItem("paymentConfirmed"),
            unlockCodeExpiry: localStorage.getItem("unlockCodeExpiry"),
        },
    };

    // Last ~120 console lines if available (installed by QR watchdog or by ensureConsoleBuffer below)
    const consoleTail = (window.__qrConsoleBuffer || []).slice(-120);
    const consoleHtml = consoleTail
        .map((e) => `<li>[${safe(dt(e.ts))}] <strong>${safe(e.level)}</strong> — ${safe(e.msg)}</li>`)
        .join("");

    const answersHtml = Object.entries(answers || {})
        .map(([k, v]) => `<li><strong>${safe(k)}</strong>: ${safe(String(v))}</li>`)
        .join("");

    return `
    <hr/>
    <h3>Diagnostik</h3>
    <ul>
      <li><strong>Kategori</strong>: ${safe(categoryKey || "n/a")}</li>
      <li><strong>Ämne</strong>: ${safe(subject || "")}</li>
      <li><strong>Enhet</strong>: ${safe(selectedFridgeId || "")}</li>
      <li><strong>Användare</strong>: ${safe(user?.id || "guest")}${
        user?.email ? ` (${safe(user.email)})` : ""
    }</li>
      <li><strong>Path</strong>: ${safe(app.path)}</li>
      <li><strong>URL</strong>: ${safe(app.url)}</li>
      <li><strong>Visibility</strong>: ${safe(app.visibility)}</li>
    </ul>

    <h4>Formulärsvar</h4>
    ${answersHtml ? `<ul>${answersHtml}</ul>` : "<p>(tomt)</p>"}

    <h4>LocalStorage</h4>
    <ul>
      ${Object.entries(app.localStorage)
        .map(([k, v]) => `<li><strong>${safe(k)}</strong>: ${safe(v)}</li>`)
        .join("")}
    </ul>

    <h4>Enhet</h4>
    <ul>
      <li><strong>UA</strong>: ${safe(device.ua)}</li>
      <li><strong>Platform</strong>: ${safe(device.platform)}</li>
      <li><strong>Språk</strong>: ${safe(device.lang)}</li>
      <li><strong>Viewport</strong>: ${safe(`${device.viewport.w}x${device.viewport.h}`)}</li>
      <li><strong>Skärm</strong>: ${safe(`${device.screen.w}x${device.screen.h} @${device.screen.dpr}x`)}</li>
      ${
        device.connection
            ? `<li><strong>Nät</strong>: ${safe(
                `type=${device.connection.effectiveType}, downlink=${device.connection.downlink}, rtt=${device.connection.rtt}, saveData=${device.connection.saveData}`
            )}</li>`
            : ""
    }
    </ul>

    <h4>Console (senaste ${consoleTail.length})</h4>
    ${consoleHtml ? `<ol>${consoleHtml}</ol>` : "<p>(tomt)</p>"}
  `;
}

export default function SupportPage() {
    const { user, token } = useAuth?.() ?? {};
    const navigate = useNavigate();
    const location = useLocation();
    const selectedFridgeId = location.state?.selectedFridgeId ?? localStorage.getItem("selectedFridgeId") ?? null;

    // Steps: 'category' -> 'details' -> 'review' -> 'success'
    const [currentStep, setCurrentStep] = useState('category');
    const [categoryKey, setCategoryKey] = useState("");
    const [answers, setAnswers] = useState({});
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [successId, setSuccessId] = useState(null);
    const [confirmNoQr, setConfirmNoQr] = useState(false);

    useEffect(() => {
        ensureConsoleBuffer(200);
    }, []);

    const selectedGroup = useMemo(
        () => QUESTION_GROUPS.find((g) => g.key === categoryKey) || null,
        [categoryKey]
    );

    const requiredOk = useMemo(() => {
        if (!selectedGroup) return true;
        return selectedGroup.fields
            .filter((f) => f.required)
            .every((f) => ((answers[f.key] ?? "").toString().trim().length > 0));
    }, [selectedGroup, answers]);

    const resetForm = () => {
        setSubject("");
        setMessage("");
        setCategoryKey("");
        setAnswers({});
        setCurrentStep('category');
        setError("");
        setSuccessId(null);
    };

    const handleCategorySelect = (key) => {
        setCategoryKey(key);
        setAnswers({});
        setCurrentStep('details');
    };

    const handleNext = () => {
        if (currentStep === 'details') {
            setCurrentStep('review');
        }
    };

    const handleBack = () => {
        if (currentStep === 'details') {
            setCurrentStep('category');
        } else if (currentStep === 'review') {
            setCurrentStep('details');
        } else {
            navigate(-1);
        }
    };

    const handleSubmit = async () => {
        if (!requiredOk && !selectedGroup) return;

        setSubmitting(true);
        setError("");

        const diagnostics = buildSupportDiagnostics({
            selectedFridgeId,
            categoryKey,
            subject,
            answers,
            user,
        });

        try {
            const resolvedSubject = selectedGroup?.subject
                ? `${selectedGroup.subject}${subject ? ` — ${subject}` : ""}`
                : subject || "Supportärende";

            const qHtml = selectedGroup
                ? `<h3>${selectedGroup.title}</h3><ul>${
                    selectedGroup.fields
                        .map((f) => {
                            const v = answers[f.key];
                            if (!v) return "";
                            const shown = f.type === "datetime" ? new Date(v).toLocaleString() : String(v);
                            return `<li><strong>${f.label}</strong><br/>${shown.replace(/\n/g, "<br/>"
                            )}</li>`;
                        })
                        .join("")
                }</ul>`
                : "";

            const intro = `<p><strong>Fråga från appen</strong></p>` +
                (selectedFridgeId ? `<p>Enhet: ${selectedFridgeId}</p>` : "");

            const free = message.trim()
                ? `<h3>Meddelande</h3><p>${message.replace(/\n/g, "<br/>")}</p>`
                : "";

            const htmlBody = `${intro}${qHtml}${free}`;
            const payload = {
                Subject: resolvedSubject,
                HtmlBody: `${htmlBody}${diagnostics}`,
            };

            const resp = await fetch(`${API_BASE}/api/support/tickets`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                const txt = await resp.text();
                throw new Error(txt || `Request failed: ${resp.status}`);
            }

            const raw = await resp.text();
            let ticketId;
            try {
                ticketId = JSON.parse(raw);
            } catch {
                ticketId = raw;
            }

            setSuccessId(ticketId);
            setCurrentStep('success');
        } catch (err) {
            setError(err.message || "Något gick fel. Försök igen.");
        } finally {
            setSubmitting(false);
        }
    };

    const renderStepIndicator = () => {
        const steps = [
            { key: 'category', label: 'Kategori', icon: faClipboardList },
            { key: 'details', label: 'Detaljer', icon: faTools },
            { key: 'review', label: 'Granska', icon: faCheckCircle }
        ];

        const getCurrentStepIndex = () => {
            if (currentStep === 'success') return 3;
            return steps.findIndex(s => s.key === currentStep);
        };

        const currentIndex = getCurrentStepIndex();

        return (
            <StepIndicatorContainer>
                <StepProgress>
                    {steps.map((step, index) => (
                        <StepItem key={step.key}>
                            <StepCircle
                                $active={index === currentIndex}
                                $completed={index < currentIndex}
                                $upcoming={index > currentIndex}
                            >
                                <FontAwesomeIcon
                                    icon={index < currentIndex ? faCheck : step.icon}
                                />
                            </StepCircle>
                            <StepLabel $active={index === currentIndex} $completed={index < currentIndex}>
                                {step.label}
                            </StepLabel>
                            {index < steps.length - 1 && (
                                <StepConnector $active={index < currentIndex} />
                            )}
                        </StepItem>
                    ))}
                </StepProgress>
                <ProgressBar>
                    <ProgressFill $progress={(currentIndex / (steps.length - 1)) * 100} />
                </ProgressBar>
            </StepIndicatorContainer>
        );
    };

    const renderCategoryStep = () => (
        <StepContent>
            <StepTitle>Vad behöver du hjälp med?</StepTitle>
            <StepSubtitle>Välj den kategori som bäst beskriver ditt problem</StepSubtitle>

            <CategoryGrid>
                {QUESTION_GROUPS.map((group) => (
                    <CategoryCard
                        key={group.key}
                        onClick={() => handleCategorySelect(group.key)}
                        $color={getCategoryColor(group.key)}
                    >
                        <CategoryIcon $color={getCategoryColor(group.key)}>
                            <FontAwesomeIcon icon={getCategoryIcon(group.key)} />
                        </CategoryIcon>
                        <CategoryContent>
                            <CategoryTitle>{group.title}</CategoryTitle>
                            <CategoryDescription>
                                {group.key === 'technical' && 'Problem med teknik eller funktioner'}
                                {group.key === 'payment' && 'Betalning och fakturering'}
                                {group.key === 'order' && 'Beställning och leverans'}
                                {group.key === 'connection' && 'Nätverks- och anslutningsproblem'}
                                {group.key === 'product' && 'Produktrelaterade frågor'}
                                {group.key === 'app' && 'Problem med appen'}
                                {group.key === 'support' && 'Allmän support och hjälp'}
                                {group.key === 'qr' && 'QR-kod fungerar inte'}
                                {group.key === 'delivery' && 'Leverans och frakt'}
                                {group.key === 'billing' && 'Faktura och betalning'}
                                {group.key === 'network' && 'Internetanslutning'}
                                {group.key === 'hardware' && 'Hårdvaruproblem'}
                                {!['technical', 'payment', 'order', 'connection', 'product', 'app', 'support', 'qr', 'delivery', 'billing', 'network', 'hardware'].includes(group.key) && 'Starta formulär'}
                            </CategoryDescription>
                        </CategoryContent>
                        <CategoryArrow>
                            <FontAwesomeIcon icon={faChevronRight} />
                        </CategoryArrow>
                    </CategoryCard>
                ))}
            </CategoryGrid>
        </StepContent>
    );

    const renderDetailsStep = () => (
        <StepContent>
            <StepTitle>{selectedGroup?.title}</StepTitle>
            <StepSubtitle>Du vet väl att du alltid kan se din QR-kod under betalningshistorik. Leta upp ditt köp och klicka på den lilla QR-ikonen för att öppna nyckeln. Om nyckeln ej fungerar, fortsätt med detta formulär.</StepSubtitle>

            <ConsentRow>
                <ConsentCheckbox
                    id="noQrConfirm"
                    type="checkbox"
                    checked={confirmNoQr}
                    onChange={(e) => setConfirmNoQr(e.target.checked)}
                />
                <ConsentLabel htmlFor="noQrConfirm">
                    Jag hittade inte QR-koden på sidan <strong>Betalningshistorik</strong>
                </ConsentLabel>
            </ConsentRow>

            <FormSection $disabled={!confirmNoQr} aria-disabled={!confirmNoQr}>
                {selectedGroup?.fields.map((field) => {
                    const value = answers[field.key] ?? "";
                    const onChange = (v) => setAnswers((prev) => ({ ...prev, [field.key]: v }));

                    return (
                        <FormField key={field.key}>
                            <FieldLabel>
                                {field.label}
                                {field.required && <RequiredAsterisk>*</RequiredAsterisk>}
                            </FieldLabel>

                            {field.type === "select" ? (
                                <StyledSelect
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    $hasError={field.required && !value}
                                    disabled={!confirmNoQr}
                                >
                                    <option value="">Välj alternativ...</option>
                                    {field.options?.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </StyledSelect>
                            ) : field.type === "textarea" ? (
                                <StyledTextarea
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    placeholder="Beskriv ditt problem så detaljerat som möjligt. Ju mer information du ger, desto bättre kan vi hjälpa dig..."
                                    rows={field.key.includes('description') || field.key.includes('details') || field.key.includes('message') ? 6 : 4}
                                    $hasError={field.required && !value}
                                    disabled={!confirmNoQr}
                                />
                            ) : field.type === "datetime" ? (
                                <StyledInput
                                    type="datetime-local"
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    $hasError={field.required && !value}
                                    disabled={!confirmNoQr}
                                />
                            ) : (
                                <StyledInput
                                    type="text"
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    placeholder="Ange information..."
                                    $hasError={field.required && !value}
                                    disabled={!confirmNoQr}
                                />
                            )}
                        </FormField>
                    );
                })}

                <FormField>
                    <FieldLabel>Ytterligare information (valfritt)</FieldLabel>
                    <StyledTextarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Beskriv ditt problem så detaljerat som möjligt. Ju mer information du ger, desto bättre kan vi hjälpa dig..."
                        rows={6}
                        disabled={!confirmNoQr}
                    />
                </FormField>

                {selectedFridgeId && (
                    <InfoBox>
                        <FontAwesomeIcon icon={faCheck} />
                        <span>Detta ärende kopplas automatiskt till enhet: {selectedFridgeId}</span>
                    </InfoBox>
                )}
            </FormSection>

        </StepContent>
    );

    const renderReviewStep = () => (
        <StepContent>
            <StepTitle>Granska ditt ärende</StepTitle>
            <StepSubtitle>Kontrollera att informationen stämmer innan du skickar</StepSubtitle>

            <ReviewCard>
                <ReviewSection>
                    <ReviewLabel>Kategori</ReviewLabel>
                    <ReviewValue>{selectedGroup?.title}</ReviewValue>
                </ReviewSection>

                {selectedGroup?.fields.map((field) => {
                    const value = answers[field.key];
                    if (!value) return null;

                    const displayValue = field.type === "datetime"
                        ? new Date(value).toLocaleString()
                        : value;

                    return (
                        <ReviewSection key={field.key}>
                            <ReviewLabel>{field.label}</ReviewLabel>
                            <ReviewValue>{displayValue}</ReviewValue>
                        </ReviewSection>
                    );
                })}

                {message && (
                    <ReviewSection>
                        <ReviewLabel>Ytterligare information</ReviewLabel>
                        <ReviewValue>{message}</ReviewValue>
                    </ReviewSection>
                )}
            </ReviewCard>

            {error && (
                <ErrorMessage>
                    <FontAwesomeIcon icon={faTimes} />
                    {error}
                </ErrorMessage>
            )}
        </StepContent>
    );

    const renderSuccessStep = () => (
        <StepContent>
            <SuccessIcon>
                <FontAwesomeIcon icon={faCheckCircle} />
            </SuccessIcon>
            <StepTitle>Ärende skapat!</StepTitle>
            <StepSubtitle>
                Ditt ärende har skapats med ID: <strong>{successId}</strong>
                <br />
                Vi återkommer via e-post inom 24 timmar.
            </StepSubtitle>

            <SuccessActions>
                <ActionButton onClick={() => navigate('/products')} $primary>
                    Tillbaka till butiken
                </ActionButton>
                <ActionButton onClick={resetForm}>
                    Skapa nytt ärende
                </ActionButton>
            </SuccessActions>
        </StepContent>
    );

    const canProceed = () => {
        if (currentStep === 'details') {
            return confirmNoQr && requiredOk; // must confirm + fill required fields
        }
        return true;
    };

    return (
        <Container>
            <ContentWrapper>
                {currentStep !== 'success' && renderStepIndicator()}

                {currentStep === 'category' && renderCategoryStep()}
                {currentStep === 'details' && renderDetailsStep()}
                {currentStep === 'review' && renderReviewStep()}
                {currentStep === 'success' && renderSuccessStep()}
            </ContentWrapper>

            {currentStep !== 'success' && (
                <FloatingActions>
                    {currentStep !== 'category' && (
                        <ActionButton onClick={handleBack}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Tillbaka
                        </ActionButton>
                    )}

                    {currentStep === 'details' && (
                        <ActionButton
                            onClick={handleNext}
                            disabled={!canProceed()}
                            $primary
                        >
                            Nästa
                            <FontAwesomeIcon icon={faChevronRight} />
                        </ActionButton>
                    )}

                    {currentStep === 'review' && (
                        <ActionButton
                            onClick={handleSubmit}
                            disabled={submitting}
                            $primary
                        >
                            {submitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Skickar...
                                </>
                            ) : (
                                <>
                                    Skicka ärende
                                    <FontAwesomeIcon icon={faCheck} />
                                </>
                            )}
                        </ActionButton>
                    )}
                </FloatingActions>
            )}

            <BottomMenuSpacer />
            <BottomMenu selectedFridgeId={selectedFridgeId} />
        </Container>
    );
}

/* ================ Animations ================ */
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

/* ================ Styled Components ================ */
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f6f7fa 0%, #e9ecef 100%);
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
`;

const StepIndicatorContainer = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  padding: 24px 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const StepProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin-bottom: 16px;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
`;

const StepCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ $active, $completed, $upcoming }) => {
    if ($completed) return 'linear-gradient(135deg, #374151 0%, #1f2937 100%)';
    if ($active) return 'linear-gradient(135deg, #374151 0%, #1f2937 100%)';
    return '#e5e7eb';
}};
  color: ${({ $active, $completed }) => ($active || $completed ? 'white' : '#9ca3af')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  margin-bottom: 12px;
  box-shadow: ${({ $active, $completed }) =>
    ($active || $completed) ? '0 4px 12px rgba(55, 65, 81, 0.3)' : 'none'
};
  transform: ${({ $active }) => ($active ? 'scale(1.1)' : 'scale(1)')};
`;

const StepLabel = styled.span`
  font-size: 13px;
  color: ${({ $active, $completed }) => {
    if ($completed || $active) return '#1f2937';
    return '#9ca3af';
}};
  font-weight: ${({ $active, $completed }) => ($active || $completed ? '600' : '400')};
  text-align: center;
  transition: all 0.3s ease;
`;

const StepConnector = styled.div`
  position: absolute;
  top: 22px;
  left: calc(50% + 22px);
  right: calc(-50% + 22px);
  height: 2px;
  background: ${({ $active }) => ($active ? '#374151' : '#e5e7eb')};
  transition: all 0.4s ease;
  z-index: -1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #374151 0%, #1f2937 100%);
  width: ${({ $progress }) => $progress}%;
  transition: width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-radius: 2px;
`;

const StepContent = styled.div`
  animation: ${fadeInUp} 0.5s ease-out;
`;

const StepTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  text-align: center;
`;

const StepSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  margin: 0 0 32px 0;
  line-height: 1.5;
  font-weight: 700;
`;

const CategoryGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CategoryCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: ${({ $color }) => $color};
    
    &:before {
      left: 100%;
    }
  }
`;

const CategoryIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(55, 65, 81, 0.3);
`;

const CategoryContent = styled.div`
  flex: 1;
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const CategoryDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const CategoryArrow = styled.div`
  color: #9ca3af;
  transition: all 0.3s ease;

  ${CategoryCard}:hover & {
    color: #6b7280;
    transform: translateX(4px);
  }
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;


const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const RequiredAsterisk = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const StyledInput = styled.input`
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1f2937;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  height: 50px;
  box-sizing: border-box;
  border: ${({ $hasError }) => ($hasError ? '1px solid #ef4444' : '1px solid #e5e7eb')};

  &:focus {
    outline: none;
    border-color: #374151;
    box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1);
    background: rgba(255, 255, 255, 1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
        background: #f3f4f6;
        cursor: not-allowed;
        color: #9ca3af;
    }
`;

const StyledSelect = styled.select`
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1f2937;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  height: 50px;
  box-sizing: border-box;
  appearance: none;
  cursor: pointer;
  text-align: left;
  border: ${({ $hasError }) => ($hasError ? '1px solid #ef4444' : '1px solid #e5e7eb')};
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: #374151;
    box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1);
    background: rgba(255, 255, 255, 1);
  }

  option {
    color: #1f2937;
    background: #ffffff;
    padding: 8px;
  }

  &:disabled {
        background: #f3f4f6;
        cursor: not-allowed;
        color: #9ca3af;
    }
`;

const StyledTextarea = styled.textarea`
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1f2937;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  min-height: ${({ rows }) => rows === 6 ? '120px' : '80px'};
  box-sizing: border-box;
  border: ${({ $hasError }) => ($hasError ? '1px solid #ef4444' : '1px solid #e5e7eb')};

  &:focus {
    outline: none;
    border-color: #374151;
    box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1);
    background: rgba(255, 255, 255, 1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
        background: #f3f4f6;
        cursor: not-allowed;
        color: #9ca3af;
    }
`;

const InfoBox = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #065f46;
  font-size: 14px;
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const ReviewSection = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ReviewLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
`;

const ReviewValue = styled.div`
  font-size: 16px;
  color: #1f2937;
  white-space: pre-wrap;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #991b1b;
  font-size: 14px;
`;

const SuccessIcon = styled.div`
  text-align: center;
  margin-bottom: 24px;
  
  svg {
    font-size: 64px;
    color: #10b981;
  }
`;

const SuccessActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
`;

const FloatingActions = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.8);
  padding: 20px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 80px;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
`;

const ActionButton = styled.button`
  padding: 16px 24px;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  
  ${({ $primary }) => $primary ? `
    background: linear-gradient(135deg, rgba(37, 37, 37, 0.95) 0%, rgba(55, 65, 81, 0.95) 100%);
    backdrop-filter: blur(8px);
    color: white;
    flex: 1;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(37, 37, 37, 0.3);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(37, 37, 37, 1) 0%, rgba(55, 65, 81, 1) 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(37, 37, 37, 0.4);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(8px);
    color: #6b7280;
    border: 1px solid rgba(107, 114, 128, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #374151;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }
  `}
`;

const BottomMenuSpacer = styled.div`
  height: 0px;
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: rgba(255,255,255,0.9);
  margin-bottom: 16px;
`;

const ConsentCheckbox = styled.input`
  transform: scale(1.1);
  margin-top: 2px;
  accent-color: #252525;
`;

const ConsentLabel = styled.label`
  font-size: 14px;
  color: #374151;
  line-height: 1.4;
  cursor: pointer;
  strong { color: #111827; }
`;
